import React, { useMemo } from 'react';
import { X, Calendar, RefreshCw, Clock, ChevronRight } from 'lucide-react';
import { format, addMonths, isAfter, isBefore, differenceInCalendarDays, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import CategoryIcon from '../common/CategoryIcon';
import SectionLabel from '../common/SectionLabel';

const formatAmount = (amount) =>
  Math.abs(amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const getIntervalLabel = (interval) => {
  if (!interval || interval === 0) return null;
  if (interval === 1) return 'Monatlich';
  if (interval === 2) return 'Alle 2 Monate';
  if (interval === 3) return 'Vierteljährlich';
  if (interval === 6) return 'Halbjährlich';
  if (interval === 12) return 'Jährlich';
  return `Alle ${interval} Monate`;
};

const getUpcomingDates = (pt, count = 3) => {
  if (!pt.date) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const results = [];

  if (!pt.interval || pt.interval === 0) {
    results.push(parseISO(pt.date));
    return results;
  }

  let current = parseISO(pt.date);
  const endDate = pt.end_date ? parseISO(pt.end_date) : null;
  let safety = 0;

  while (results.length < count && safety < 200) {
    safety++;
    if (endDate && isAfter(current, endDate)) break;
    if (!isBefore(current, today)) results.push(new Date(current));
    current = addMonths(current, pt.interval);
  }

  return results;
};

const datePillLabel = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = differenceInCalendarDays(date, today);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  if (diff === -1) return 'Gestern';
  if (diff < 0) return `Vor ${Math.abs(diff)} Tagen`;
  if (diff <= 30) return `In ${diff} Tagen`;
  return format(date, 'd. MMM', { locale: de });
};

const isOverdue = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return differenceInCalendarDays(date, today) < 0;
};

const Row = ({ pt, onEdit }) => {
  const isExpense = pt.amount < 0;
  const upcomingDates = getUpcomingDates(pt, 3);
  const intervalLabel = getIntervalLabel(pt.interval);
  const firstCat = pt.categories?.[0];
  const hasRow2 = upcomingDates.length > 0 || intervalLabel || !pt.date;

  return (
    <div className="group flex items-center gap-2 bg-white border border-neutral-100 rounded-xl hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-150 shadow-sm">
      <div
        onClick={() => onEdit && onEdit(pt)}
        className="flex-1 min-w-0 space-y-0.5 px-3 py-2.5 cursor-pointer"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {firstCat && (
              <CategoryIcon name={firstCat.icon_name} size={14} className="text-neutral-400 flex-shrink-0" />
            )}
            <span className="text-sm font-semibold text-neutral-800 truncate">
              {pt.name}
            </span>
          </div>
          <span className={`flex-none text-sm font-bold tabular-nums ${isExpense ? 'text-red-600' : 'text-emerald-600'}`}>
            {isExpense ? '−' : '+'}{formatAmount(pt.amount)}
          </span>
        </div>

        {hasRow2 && (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1 flex-wrap">
              {upcomingDates.length > 0 ? (
                upcomingDates.map((d, i) => {
                  const overdue = isOverdue(d);
                  return (
                    <span
                      key={i}
                      className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md leading-none ${
                        overdue
                          ? 'bg-red-50 text-red-500'
                          : 'bg-primary-50 text-primary-600'
                      }`}
                    >
                      {datePillLabel(d)}
                    </span>
                  );
                })
              ) : !pt.date ? (
                <span className="text-[11px] text-neutral-400 italic">Kein Datum</span>
              ) : null}
              {intervalLabel && upcomingDates.length === 3 && (
                <span className="text-[11px] text-neutral-400">…</span>
              )}
            </div>
            {intervalLabel && (
              <span className="flex-none flex items-center gap-1 text-[11px] text-neutral-500 font-medium whitespace-nowrap">
                {pt.end_date && (
                  <span className="text-neutral-400 font-normal">
                    bis {format(parseISO(pt.end_date), 'dd. MMM yyyy', { locale: de })} ·
                  </span>
                )}
                <RefreshCw size={10} />
                {intervalLabel}
              </span>
            )}
          </div>
        )}
      </div>
      <ChevronRight size={14} className="text-neutral-300 group-hover:text-primary-400 transition-colors flex-shrink-0 mr-2" />
    </div>
  );
};

const Section = ({ title, icon: Icon, items, onEdit }) => {
  if (items.length === 0) return null;
  return (
    <div className="mb-5">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={13} className="text-neutral-400" />
        <SectionLabel as="span">{title}</SectionLabel>
      </div>
      <div className="space-y-1.5">
        {items.map(pt => (
          <Row key={pt.id} pt={pt} onEdit={onEdit} />
        ))}
      </div>
    </div>
  );
};

const UpcomingTransactionsModal = ({ isOpen, onClose, transactions, onEdit }) => {
  const { recurring, oneTime, pending } = useMemo(() => {
    const recurring = [];
    const oneTime = [];
    const pending = [];

    (transactions || []).forEach((pt) => {
      if (pt.is_booked) return;
      if (!pt.date) {
        pending.push(pt);
      } else if (pt.is_planned || (pt.interval && pt.interval > 0)) {
        recurring.push(pt);
      } else {
        oneTime.push(pt);
      }
    });

    const byDate = (a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(a.date) - new Date(b.date);
    };

    return {
      recurring: recurring.sort(byDate),
      oneTime: oneTime.sort(byDate),
      pending,
    };
  }, [transactions]);

  const totalUpcoming = (transactions || []).filter(t => !t.is_booked).length;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center md:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl w-full md:max-w-xl max-h-[88vh] md:max-h-[85vh] flex flex-col overflow-hidden border border-neutral-100 rounded-t-3xl md:rounded-3xl animate-sheet-up md:animate-none pb-[env(safe-area-inset-bottom)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-3 pb-1 md:hidden flex-none">
          <div className="w-10 h-1 bg-neutral-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 flex-none">
          <div className="flex items-center gap-2">
            <div>
              <h2 className="text-lg font-bold text-neutral-800">Geplante Einträge</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-xl transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {totalUpcoming === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4">
                <Calendar size={28} className="text-neutral-300" />
              </div>
              <p className="font-semibold text-neutral-500 mb-1 text-sm">Keine geplanten Einträge</p>
            </div>
          ) : (
            <>
              <Section title="Vorgemerkt" icon={Clock} items={pending} onEdit={onEdit} />
              <Section title="Einmalig geplant" icon={Calendar} items={oneTime} onEdit={onEdit} />
              <Section title="Wiederkehrend" icon={RefreshCw} items={recurring} onEdit={onEdit} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingTransactionsModal;
